import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import { LayoutList, Smile } from 'lucide-react';
import { SwitchSpinner } from '../shared/SwitchSpinner';
import { useTranslation } from 'react-i18next';
import { runWithHeldContentFade } from '../../utils/pageCrossfade';
import {
  LEARNER_HOME_DESIGN_USER_CHANGED_EVENT,
  useLearnerHomeDesign,
  type LearnerHomeDesign,
} from '../../utils/learnerHomeDesign';

const headerChromeIconBtn =
  'header-chrome-icon-btn flex shrink-0 items-center justify-center border-0 bg-transparent p-0 shadow-none outline-none ring-0 transition-opacity [-webkit-tap-highlight-color:transparent] hover:opacity-75 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 active:opacity-90';

/**
 * Template switch. Same control as light/dark: one icon, click swaps to the other.
 * Sits under the mode icon, inside the header.
 */
export const LearnerHomeDesignToggle: React.FC = () => {
  const { t } = useTranslation('settings');
  const [design, setDesign] = useLearnerHomeDesign();
  const [pending, setPending] = useState(false);
  const next = design === 'classic' ? 'mme' : 'classic';
  const label = t(next === 'mme' ? 'homeDesignMmeShort' : 'homeDesignClassicShort', {
    defaultValue: next === 'mme' ? 'Simple - easy to use' : 'Advanced - more details',
  });
  const Icon = design === 'classic' ? Smile : LayoutList;

  const switchNow = () => {
    if (pending) return;
    const target: LearnerHomeDesign = next;
    setPending(true);
    void runWithHeldContentFade(() => {
      flushSync(() => {
        const root = document.documentElement;
        if (target === 'mme') root.dataset.learnerDesign = 'mme';
        else delete root.dataset.learnerDesign;
        setDesign(target);
      });
    }, () => {
      window.dispatchEvent(new CustomEvent(LEARNER_HOME_DESIGN_USER_CHANGED_EVENT, { detail: target }));
    }).finally(() => setPending(false));
  };

  return (
    <button
      type="button"
      data-tour-id="mme-design-bar"
      onClick={switchNow}
      aria-busy={pending}
      aria-disabled={pending}
      className={`${headerChromeIconBtn} h-7 w-11 text-[#0a0a0a] dark:text-white ${pending ? 'cursor-default' : ''}`}
      aria-label={label}
      title={label}
    >
      {pending ? <SwitchSpinner /> : <Icon className="h-5 w-5" aria-hidden />}
    </button>
  );
};
